(() => {
  'use strict';

  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  function loadImage(source) {
    if (!source) return Promise.reject(new Error('Missing image source'));
    const url = source.startsWith('//') ? `https:${source}` : source;
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error(`Unable to load image: ${url}`));
      image.src = url;
    });
  }

  function fitCanvas(canvas) {
    if (!canvas || !canvas.parentElement) return;
    const height = canvas.parentElement.offsetHeight;
    canvas.width = height * 1.7094;
    canvas.height = height;
  }

  function createCanvasController({ canvases, render }) {
    const resize = () => {
      canvases.forEach(fitCanvas);
      render();
    };
    const observer = new ResizeObserver(resize);
    return {
      init() {
        canvases.filter(Boolean).forEach(canvas => observer.observe(canvas.parentElement));
        resize();
      },
      destroy() { observer.disconnect(); }
    };
  }

  function initTooltips(root) {
    const tooltip = qs('#tooltip-global');
    if (!tooltip) return;
    root.addEventListener('pointerover', event => {
      const wrapper = event.target.closest('.tooltip-wrapper');
      if (!wrapper || !root.contains(wrapper)) return;
      const content = qs('.tooltip-content', wrapper);
      if (!content) return;
      const rect = wrapper.getBoundingClientRect();
      tooltip.innerHTML = content.innerHTML;
      tooltip.hidden = false;
      tooltip.style.left = `${rect.left + rect.width / 2 + window.scrollX}px`;
      tooltip.style.top = `${rect.top + window.scrollY - tooltip.offsetHeight - 10}px`;
    });
    root.addEventListener('pointerout', event => {
      if (event.target.closest('.tooltip-wrapper')) tooltip.hidden = true;
    });
  }

  function initModals(root) {
    root.addEventListener('click', event => {
      const opener = event.target.closest('.open-modal');
      const closer = event.target.closest('[data-modal-close], .custom-modal__close, .custom-modal__overlay');
      if (opener) {
        event.preventDefault();
        const modal = qs(opener.dataset.src);
        if (modal) modal.hidden = false;
      }
      if (closer) {
        const modal = closer.closest('.custom-modal');
        if (modal) modal.hidden = true;
      }
    });
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape') qsa('.custom-modal:not([hidden])').forEach(modal => { modal.hidden = true; });
    });
  }

  function createStepNavigator({ root, mandatorySteps = [], validateStep = () => true }) {
    const steps = qsa('.step', root);
    const titles = qsa('.step-title-nav', root);
    let index = 0;

    function show(nextIndex, direction = 'next') {
      if (nextIndex < 0 || nextIndex >= steps.length || nextIndex === index) return;
      const current = steps[index];
      if (direction === 'next' && mandatorySteps.includes(current.dataset.step) && !validateStep(current)) return;
      current.classList.remove('active');
      titles[index]?.classList.remove('active');
      index = nextIndex;
      steps[index].classList.add('active');
      titles[index]?.classList.add('active');
      root.dispatchEvent(new CustomEvent('belt:stepchange', { detail: { index, step: steps[index] } }));
    }

    function init() {
      steps[0]?.classList.add('active');
      titles[0]?.classList.add('active');
      root.addEventListener('click', event => {
        const arrow = event.target.closest('.nav-arrow:not([disabled])');
        if (!arrow) return;
        const direction = arrow.dataset.dir;
        show(index + (direction === 'next' ? 1 : -1), direction);
      });
      qsa('.recap-item', root).forEach((item, itemIndex) => item.addEventListener('click', () => show(itemIndex, itemIndex > index ? 'next' : 'prev')));
    }
    return { init, show, get index() { return index; }, get steps() { return steps; } };
  }

  function initMobileNavigation({ root, navigator }) {
    let startX = 0;
    let startY = 0;
    const hint = qs('#scroll-hint', root);
    const refreshHint = () => {
      if (!hint) return;
      const step = navigator.steps[navigator.index];
      const canScroll = step && step.scrollTop + step.clientHeight < step.scrollHeight - 6;
      hint.classList.toggle('visible', matchMedia('(max-width: 768px)').matches && canScroll);
    };
    root.addEventListener('touchstart', event => {
      if (event.touches.length !== 1) return;
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
    }, { passive: true });
    root.addEventListener('touchend', event => {
      if (event.changedTouches.length !== 1) return;
      const dx = event.changedTouches[0].clientX - startX;
      const dy = event.changedTouches[0].clientY - startY;
      if (Math.abs(dx) < 55 || Math.abs(dx) <= Math.abs(dy) * 1.35) return;
      navigator.show(navigator.index + (dx < 0 ? 1 : -1), dx < 0 ? 'next' : 'prev');
    }, { passive: true });
    navigator.steps.forEach(step => step.addEventListener('scroll', refreshHint, { passive: true }));
    root.addEventListener('belt:stepchange', () => requestAnimationFrame(refreshHint));
    window.addEventListener('resize', refreshHint, { passive: true });
    refreshHint();
  }

  // Touch navigation works at every viewport width, including landscape tablets.
  function initStepSwipeNavigation(root, navigate) {
    if (!root || root.dataset.stepSwipeBound === 'true') return;
    root.dataset.stepSwipeBound = 'true';

    let gesture = null;

    const ignoreTarget = target => {
      if (target.closest('button, a, input, textarea, select, label, #recap-bar, .custom-modal')) {
        return true;
      }

      // Keep native horizontal scrolling for overflowing buckle/category rows.
      for (let element = target; element && root.contains(element); element = element.parentElement) {
        const overflowX = window.getComputedStyle(element).overflowX;
        if (/^(auto|scroll)$/.test(overflowX) && element.scrollWidth > element.clientWidth + 4) {
          return true;
        }
        if (element === root) break;
      }
      return false;
    };

    root.addEventListener('touchstart', event => {
      gesture = null;
      if (event.touches.length !== 1 || ignoreTarget(event.target)) return;
      const touch = event.touches[0];
      gesture = {
        id: touch.identifier,
        x: touch.clientX,
        y: touch.clientY,
        startedAt: Date.now(),
        horizontal: false
      };
    }, { passive: true });

    root.addEventListener('touchmove', event => {
      if (!gesture) return;
      if (event.touches.length !== 1) {
        gesture = null;
        return;
      }
      const touch = event.touches[0];
      if (touch.identifier !== gesture.id) return;
      const dx = Math.abs(touch.clientX - gesture.x);
      const dy = Math.abs(touch.clientY - gesture.y);
      if (dx > 12 && dx > dy * 1.35) {
        gesture.horizontal = true;
        if (event.cancelable) event.preventDefault();
      }
    }, { passive: false });

    root.addEventListener('touchend', event => {
      const completed = gesture;
      gesture = null;
      if (!completed || event.touches.length) return;
      const touch = Array.from(event.changedTouches).find(item => item.identifier === completed.id);
      if (!touch) return;
      const dx = touch.clientX - completed.x;
      const dy = touch.clientY - completed.y;
      if (!completed.horizontal || Date.now() - completed.startedAt > 700 ||
          Math.abs(dx) < 55 || Math.abs(dx) <= Math.abs(dy) * 1.35) return;
      navigate(dx < 0 ? 'next' : 'prev');
    }, { passive: true });

    root.addEventListener('touchcancel', () => { gesture = null; }, { passive: true });
  }

  function initHorizontalScrollHint(root, rowSelector = '#step-buckle .buckle-row') {
    const row = qs(rowSelector, root || document);
    if (!row || row.dataset.horizontalScrollHintBound === 'true') return;

    const step = row.closest('.step');
    if (!step) return;

    row.dataset.horizontalScrollHintBound = 'true';

    const hint = document.createElement('button');
    const pageLanguage = (document.documentElement.lang || '').toLowerCase();
    hint.type = 'button';
    hint.className = 'belt-horizontal-scroll-hint';
    hint.setAttribute(
      'aria-label',
      pageLanguage.startsWith('fr') ? 'Afficher plus d’options' : 'Show more options'
    );
    hint.setAttribute('aria-hidden', 'true');
    hint.tabIndex = -1;
    hint.innerHTML = '<span>›</span>';
    step.appendChild(hint);

    hint.addEventListener('click', event => {
      event.preventDefault();
      const maxScroll = Math.max(0, row.scrollWidth - row.clientWidth);
      const nextPosition = Math.min(
        maxScroll,
        Math.abs(row.scrollLeft) + Math.max(180, Math.round(row.clientWidth * 0.7))
      );

      row.scrollTo({ left: nextPosition, behavior: 'smooth' });
    });

    const refresh = () => {
      const maxScroll = Math.max(0, row.scrollWidth - row.clientWidth);
      const hasOverflow = maxScroll > 4;
      const isAtEnd = Math.abs(row.scrollLeft) >= maxScroll - 6;
      const isVisibleStep = step.classList.contains('active');

      const stepRect = step.getBoundingClientRect();
      const rowRect = row.getBoundingClientRect();
      const relativeMiddle = rowRect.top - stepRect.top + rowRect.height / 2;
      const safeTop = Math.max(54, Math.min(step.clientHeight - 40, relativeMiddle));

      const shouldShow = hasOverflow && !isAtEnd && isVisibleStep;

      hint.style.top = `${safeTop}px`;
      hint.classList.toggle('visible', shouldShow);
      hint.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      hint.tabIndex = shouldShow ? 0 : -1;
    };

    row.addEventListener('scroll', refresh, { passive: true });
    window.addEventListener('resize', refresh, { passive: true });
    window.addEventListener('load', refresh, { once: true });

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(refresh);
      resizeObserver.observe(row);
      Array.from(row.children).forEach(child => resizeObserver.observe(child));
    }

    const mutationObserver = new MutationObserver(() => requestAnimationFrame(refresh));
    mutationObserver.observe(step, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true
    });

    requestAnimationFrame(refresh);
  }

  function updateRecap(key, value) {
    const id = `recap${key.trim().split(/\s+/).map(word => word[0].toUpperCase() + word.slice(1)).join('')}`;
    const output = document.getElementById(id);
    if (output) output.textContent = value ?? '—';
  }

  /**
   * Submit the customizer independently from theme product-form components.
   * Some themes rebuild the /cart/add request with only variant and quantity;
   * collecting the controls again here guarantees line-item properties survive.
   */
  function bindCartSubmit({ form, root, beforeSubmit, syncProperties }) {
    if (!form || form.dataset.beltCartSubmit === 'ready') return;
    form.dataset.beltCartSubmit = 'ready';

    const submitButton = qs('[type="submit"]', form) || qs(`[type="submit"][form="${form.id}"]`);
    const setSubmitting = submitting => {
      if (submitting) {
        form.dataset.beltSubmitting = 'true';
      } else {
        delete form.dataset.beltSubmitting;
      }

      if (!submitButton) return;
      submitButton.disabled = submitting;
      submitButton.classList.toggle('is-loading', submitting);
      if (submitting) {
        submitButton.setAttribute('aria-busy', 'true');
      } else {
        submitButton.removeAttribute('aria-busy');
      }
    };

    // Browser back navigation may restore the page while its previous request
    // was in progress, including the disabled button and submission guard.
    window.addEventListener('pageshow', () => setSubmitting(false));

    form.addEventListener('submit', async event => {
      event.preventDefault();
      event.stopImmediatePropagation();

      if (form.dataset.beltSubmitting === 'true') return;
      if (beforeSubmit && beforeSubmit(event)) return;
      if (!form.reportValidity()) return;

      try {
        if (syncProperties && syncProperties() === false) {
          throw new Error('Please select all required belt options.');
        }

        const formData = new FormData(form);
        const propertyControls = qsa('[name^="properties["]', root || form);

        propertyControls.forEach(control => {
          if (control.disabled || !control.name) return;
          if ((control.type === 'checkbox' || control.type === 'radio') && !control.checked) return;
          formData.set(control.name, control.value);
        });

        /* Validation and property synchronization succeeded: submission is real. */
        setSubmitting(true);

        const shopRoot = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${shopRoot}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || error.message || 'Unable to add this belt to the cart.');
        }

        window.location.assign(`${shopRoot}cart`);
      } catch (error) {
        setSubmitting(false);
        window.alert(error.message || 'Unable to add this belt to the cart.');
      }
    }, true);
  }

  function syncCartPreview({ root, mode, layers = [], initials = {} }) {
    const form = qs('form[action*="/cart/add"]', root || document);
    if (!form) return;

    const values = {
      _belt_preview_mode: mode || '',
      _belt_preview_initials: initials.letters || '',
      _belt_preview_initials_points: initials.withPoints ? '1' : '',
      _belt_preview_initials_position: initials.position || '',
      _belt_preview_initials_texture: initials.texture || ''
    };

    for (let index = 0; index < 8; index += 1) {
      values[`_belt_preview_layer_${index + 1}`] = layers[index] || '';
    }

    Object.entries(values).forEach(([key, value]) => {
      const name = `properties[${key}]`;
      let input = qsa('input[type="hidden"]', root || form).find(field => field.name === name);

      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.setAttribute('form', form.id);
        form.appendChild(input);
      }

      input.value = value;
    });
  }

  function selectDefaultInitialsColor(root = document) {
    const options = qsa('#initials-details .initials-color .option', root);
    if (!options.length) return null;

    const normalize = value => (value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
    const silver = options.find(option => {
      const color = normalize(option.dataset.color || option.textContent);
      return color.includes('silver') || color.includes('argent');
    });
    const fallback = options.find(option => {
      const color = normalize(option.dataset.color || option.textContent);
      return !color.includes('neutral') && !color.includes('neutre');
    });
    const selected = silver || fallback || options[0];

    options.forEach(option => option.classList.toggle('active', option === selected));
    return selected;
  }

  function initViewSwitches(root, onFaceChange) {
    root.addEventListener('click', event => {
      const face = event.target.closest('[data-face]');
      const preview = event.target.closest('[data-preview]');
      if (face) {
        qsa('[data-face]', root).forEach(button => button.classList.toggle('active', button === face));
        onFaceChange(face.dataset.face);
      }
      if (preview) {
        qsa('[data-preview]', root).forEach(button => button.classList.toggle('active', button === preview));
        qsa('.preview-wrapper', root).forEach(wrapper => wrapper.classList.toggle('active', wrapper.id === `preview-${preview.dataset.preview}`));
      }
    });
  }

  /*
   * Critical controls use document-level capture listeners so they continue to
   * work even if a page-specific customizer initializer stops early.
   */
  function initCriticalControls() {
    if (document.documentElement.dataset.beltCriticalControls === 'ready') return;
    document.documentElement.dataset.beltCriticalControls = 'ready';

    document.addEventListener('click', event => {
      const opener = event.target.closest('.open-modal[data-src]');
      const closer = event.target.closest('.custom-modal__close, .custom-modal__overlay, [data-modal-close]');

      if (opener) {
        event.preventDefault();
        const modal = qs(opener.dataset.src);
        if (!modal) return;
        modal.hidden = false;
        modal.removeAttribute('hidden');
        modal.style.setProperty('display', 'block', 'important');
        document.body.classList.add('belt-modal-open');
      }

      if (closer) {
        const modal = closer.closest('.custom-modal');
        if (!modal) return;
        modal.style.setProperty('display', 'none', 'important');
        modal.hidden = true;
        document.body.classList.remove('belt-modal-open');
      }
    }, true);

    document.addEventListener('change', event => {
      if (!event.target.matches('#toggle-initials')) return;
      const toggle = event.target;
      const details = qs('#initials-details');
      const letters = details && qs('.initials-letters input', details);
      if (details) details.classList.toggle('hidden', !toggle.checked);
      if (letters) letters.required = toggle.checked;
      document.dispatchEvent(new CustomEvent('belt:initials-toggle', {
        detail: { checked: toggle.checked }
      }));
    }, true);
  }

  function centerMobileStepNavigation() {
    if (!window.matchMedia('(max-width: 768px)').matches) return;
    const nav = qs('#step-navigation');
    const track = nav && qs('.step-nav-titles', nav);
    const active = track && qs('.step-title-nav.active', track);
    if (!active || !nav.clientWidth) return;
    const trackRect = track.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    // Relative coordinates cancel the track's existing animated translation.
    const titleCenter = activeRect.left - trackRect.left + activeRect.width / 2;
    track.style.transform = `translateX(${nav.clientWidth / 2 - titleCenter}px)`;
  }

  function initNavigationCentering() {
    const nav = qs('#step-navigation');
    const track = nav && qs('.step-nav-titles', nav);
    if (!track || track.dataset.centeringBound) return;
    track.dataset.centeringBound = 'true';
    let frame;
    const refresh = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(centerMobileStepNavigation);
    };
    new MutationObserver(refresh).observe(track, {
      subtree: true, attributes: true, attributeFilter: ['class'], childList: true
    });
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(refresh);
      observer.observe(nav);
      observer.observe(track);
    }
    window.addEventListener('resize', refresh, { passive: true });
    if (document.fonts) document.fonts.ready.then(refresh);
    refresh();
  }

  initCriticalControls();
  initNavigationCentering();
  document.addEventListener('DOMContentLoaded', initNavigationCentering, { once: true });

  window.BeltCustomizerCore = {
    qs, qsa, loadImage, fitCanvas, createCanvasController, createStepNavigator,
    initMobileNavigation, initStepSwipeNavigation, initModals, initTooltips, initViewSwitches,
    initCriticalControls, initHorizontalScrollHint, updateRecap, bindCartSubmit, syncCartPreview,
    selectDefaultInitialsColor, centerMobileStepNavigation
  };
})();
